// «Сходимость» tab — the «винегрет» v2 layout. Owns the threshold +
// enabled-config state (shared by the curve, the summary KPI and the matrix)
// and assembles the sections in the agreed order:
//
//   1. KPI cards + honesty + time-to-threshold histogram   (ConvergenceSummary)
//   2. провалы по категориям                               (REUSE FailureBreakdownStrip)
//   3. медианные кривые + порог-контрол                    (ConvergenceCurve)
//   4. активность по дням                                  (REUSE ActivityHeatmap)
//   5. матрица станция × конфиг                            (ConvergenceHeatmap)
//   6. список серий + inline-аккордеон                     (ConvergenceSeriesList)
//
// The aggregate / series-list / matrix come through hooks under the
// ['analytics'] namespace so the screen-shell's 30-s auto-refresh reaches
// them. The histogram is derived CLIENT-side from the matrix's per-cell
// curves (no separate endpoint) so the operator's threshold stays interactive.
// The per-series drill curves are fetched lazily inside ConvergenceSeriesList
// (one request per config, session-cached). The matrix metric/scale are
// operator toggles in the matrix header.

import { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from '../../api/client';
import { FailureBreakdownStrip } from '../FailureBreakdownStrip';
import { ActivityHeatmap } from './ActivityHeatmap';
import { ConvergenceSummary, type TimeBucket, type ConvergenceKpi } from './ConvergenceSummary';
import { ConvergenceCurve } from './ConvergenceCurve';
import { ConvergenceHeatmap } from './ConvergenceHeatmap';
import { ConvergenceSeriesList } from './ConvergenceSeriesList';
import { median, timeToThreshold } from './convergenceScales';
import {
  useConvergenceAggregate,
  useConvergenceSeriesList,
  useConvergenceHeatmap,
  useAnalyticsActivity,
} from '../../hooks/useAnalytics';

import './AnalyticsScreen.css';
import './analytics-tabs.css';
import './convergence.css';

export function ConvergenceTab() {
  const scrollRef = useRef<HTMLDivElement>(null);

  const aggregate = useConvergenceAggregate();
  const seriesList = useConvergenceSeriesList();
  const heatmap = useConvergenceHeatmap();
  const activity = useAnalyticsActivity();
  const failureBreakdown = useQuery({
    queryKey: ['analytics', 'failure-breakdown'],
    queryFn: () => api.getFailureBreakdown(),
    staleTime: 30_000,
  });

  // ── shared controls ──
  const configs = aggregate.data?.configs ?? [];
  const lengths = aggregate.data?.lengths_h ?? [];
  const [enabled, setEnabled] = useState<Set<string>>(() => new Set(['all4', 'gps', 'gal']));
  const [threshold, setThreshold] = useState(10);
  const [custom, setCustom] = useState('');
  // Matrix → list drill: tapping a station in the matrix opens its latest
  // series' accordion in the list below (nonce retriggers the same station).
  const [focus, setFocus] = useState<{ tag: string; station: string; nonce: number } | null>(null);
  // C1.2 — stable identity so React.memo(ConvergenceHeatmap) can shallow-compare
  // its props. Rebuilds only when the series list actually changes (structural
  // sharing keeps `seriesList.data?.series` identity stable across polls).
  const onStationTap = useCallback(
    (station: string) => {
      const matches = (seriesList.data?.series ?? []).filter(
        (s) => s.station_code === station,
      );
      if (matches.length === 0) return;
      const latest = [...matches].sort((a, b) => (a.obs_date < b.obs_date ? 1 : -1))[0]!;
      setFocus((f) => ({ tag: latest.tag, station, nonce: (f?.nonce ?? 0) + 1 }));
    },
    [seriesList.data?.series],
  );

  const toggleConfig = (k: string) =>
    setEnabled((prev) => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  const onPreset = (mm: number) => {
    setThreshold(mm);
    setCustom('');
  };
  const onCustom = (raw: string) => {
    setCustom(raw);
    const p = parseFloat(raw);
    if (Number.isFinite(p)) setThreshold(Math.max(1, Math.min(60, p)));
  };

  // KPI derived once from the aggregate (shared with the curve).
  const kpi: ConvergenceKpi = useMemo(() => {
    const crossings = new Map<string, number | null>();
    for (const c of configs) {
      const vals = lengths.map((l) => {
        const p = c.points.find((pt) => pt.length_nominal_h === l);
        return p ? p.median_dr_mm : null;
      });
      crossings.set(c.config, timeToThreshold(vals, threshold, lengths));
    }
    const enabledKeys = configs.map((c) => c.config).filter((k) => enabled.has(k));
    const enabledCross = enabledKeys
      .map((k) => crossings.get(k))
      .filter((x): x is number => x != null);
    let bestKey: string | null = null;
    let slowKey: string | null = null;
    for (const k of enabledKeys) {
      const c = crossings.get(k);
      if (c == null) {
        slowKey = k;
        continue;
      }
      if (bestKey == null || c < (crossings.get(bestKey) ?? Infinity)) bestKey = k;
      if (slowKey == null) slowKey = k;
      else {
        const cs = crossings.get(slowKey);
        if (cs != null && c > cs) slowKey = k;
      }
    }
    return {
      medianTime: median(enabledCross),
      bestKey,
      bestTime: bestKey ? crossings.get(bestKey) ?? null : null,
      slowKey,
      slowTime: slowKey ? crossings.get(slowKey) ?? null : null,
      enabledCount: enabledKeys.length,
    };
  }, [configs, lengths, enabled, threshold]);

  // Time-to-threshold histogram — derived client-side from the matrix's
  // per-(station×config) curves at the operator's current threshold, so the
  // distribution reacts live to a threshold drag (no backend round-trip).
  const buckets: TimeBucket[] = useMemo(() => {
    const hm = heatmap.data;
    if (!hm) return [];
    const times: number[] = [];
    let never = 0;
    for (const cell of hm.cells) {
      if (cell.state !== 'curve' || !cell.curve) continue;
      const t = timeToThreshold(cell.curve, threshold, hm.lengths);
      if (t == null) never += 1;
      else times.push(t);
    }
    const total = times.length + never;
    if (total === 0) return [];
    const defs: { range: string; test: (h: number) => boolean }[] = [
      { range: '< 3 ч', test: (h) => h < 3 },
      { range: '3–6 ч', test: (h) => h >= 3 && h < 6 },
      { range: '6–12 ч', test: (h) => h >= 6 && h < 12 },
      { range: '12–24 ч', test: (h) => h >= 12 && h <= 24 },
      { range: '> 24 ч', test: (h) => h > 24 },
    ];
    const out: TimeBucket[] = defs.map((d) => {
      const count = times.filter((t) => d.test(t)).length;
      return { range: d.range, count, pct: Math.round((count / total) * 100) };
    });
    if (never > 0) {
      out.push({
        range: `не достигает ${threshold} мм`,
        count: never,
        pct: Math.round((never / total) * 100),
      });
    }
    return out.filter((b) => b.count > 0);
  }, [heatmap.data, threshold]);

  // ── loading / error gates ──
  if (aggregate.isLoading) {
    return <div className="at-empty">загрузка сходимости…</div>;
  }
  if (aggregate.isError || !aggregate.data) {
    return (
      <div className="at-empty">
        ошибка загрузки сходимости:{' '}
        {(aggregate.error as Error)?.message ?? 'неизвестная'}
      </div>
    );
  }

  return (
    <div
      className="analytics-screen at-stack conv-tab"
      ref={scrollRef}
      data-testid="convergence-agg-tab"
    >
      {/* 1. KPI + honesty + histogram */}
      <ConvergenceSummary
        summary={aggregate.data.summary}
        threshold={threshold}
        kpi={kpi}
        buckets={buckets}
      />

      {/* 2. провалы по категориям (reused) */}
      <section data-testid="convergence-failure-breakdown">
        <h3 className="at-sec-title">провалы по категориям</h3>
        <FailureBreakdownStrip breakdown={failureBreakdown.data} />
      </section>

      {/* 3. медианные кривые + порог */}
      <ConvergenceCurve
        lengths={lengths}
        configs={configs}
        enabled={enabled}
        threshold={threshold}
        customThreshold={custom}
        onToggleConfig={toggleConfig}
        onPreset={onPreset}
        onCustomThreshold={onCustom}
      />

      {/* 4. активность по дням (reused) */}
      {activity.data && (
        <ActivityHeatmap
          byCompletion={activity.data.by_completion}
          byObservation={activity.data.by_observation}
        />
      )}

      {/* 5. матрица станция × конфиг */}
      {heatmap.data && heatmap.data.stations.length > 0 && (
        <ConvergenceHeatmap
          data={heatmap.data}
          threshold={threshold}
          onStationTap={onStationTap}
        />
      )}

      <div className="section-rule" />

      {/* 6. список серий + inline-аккордеон */}
      {seriesList.data && (
        <ConvergenceSeriesList
          list={seriesList.data.series}
          scrollRef={scrollRef}
          focusTag={focus?.tag ?? null}
          focusStation={focus?.station ?? null}
          focusNonce={focus?.nonce ?? 0}
        />
      )}
    </div>
  );
}
